import React, { FC, useCallback, useState } from 'react';
import { observer } from 'mobx-react';
import { WidgetBase } from 'src/libs/ui-kit/widget-base/WidgetBase';
import { useStore } from 'src/modules/users/hooks/useStore';
import { UserOrdersStoreContext } from '../../UserOrders.widget';
import { OrderRow } from '../tables/OrderRow';
import { IUserOrdersWidgetProps } from '../../interfaces';
import { WidgetTable } from 'src/libs/ui-kit/widget-table/WidgetTable';
import { ListContainer } from 'src/components/Containers/ListContainer/ListContainer';
import { EHandlerListType } from '../../../../../../interfaces';
import { PaginationField, TPagination } from '@qlean/admin-web-list-view';
import styles from './styles.module.scss';
import { Button, ButtonGroup, Col, Container, Dropdown, Row } from 'react-bootstrap';
import { MultiSuggestField } from '../../../../../../libs/ui-kit/inputs/Suggest';
import { OrderStatusToTextMapping } from '../../../../orders/pages/order-view/constants';
import { Form, Formik } from 'formik';
import { useHistory } from 'react-router-dom';

type ProductToCreateSelectValue = { name: string; value: string };

export const CustomerOrdersWidget: FC<IUserOrdersWidgetProps> = observer(({ col, title, canCreateOrders = true }) => {
  const store = useStore(UserOrdersStoreContext);
  const { areOrdersLoading, getOrdersInformation, hasError, limit, page, total, updateLoadParams } = store;
  let pagination: TPagination = { limit, page, total };
  const history = useHistory();

  const handlePagination = useCallback((pagination: TPagination) => {
    updateLoadParams({ type: EHandlerListType.pagination, value: { pagination } });
  }, []);

  const handleOnFiltersApply = useCallback(
    ({ statuses }: { statuses: { key: string; value: string }[] }) => {
      pagination = { limit, page: 1 };
      store.filters.statuses = statuses?.map(({ value }) => Number(value));
      updateLoadParams({ type: EHandlerListType.pagination, value: { pagination } });
    },
    [updateLoadParams, limit],
  );

  const handleOnCreateOrder = useCallback(
    async (product: ProductToCreateSelectValue) => {
      const serialNumber = await store.createOrder(product.value);
      if (serialNumber) {
        history.push(`/users/${store.userId}/orders/${serialNumber}`);
      }
    },
    [history],
  );

  const renderCreateNewOrderBtn = useCallback(() => {
    if (!canCreateOrders) {
      return <div />;
    }

    return (
      <Dropdown>
        <Dropdown.Toggle disabled={store.isCreatingOrder}>Создать заказ</Dropdown.Toggle>

        <Dropdown.Menu>
          {store.availableTabs.map((product) => (
            <Dropdown.Item onClick={() => handleOnCreateOrder(product)}>{product.name}</Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    );
  }, [store.availableTabs, store.isCreatingOrder]);

  return (
    <Formik
      onSubmit={handleOnFiltersApply}
      initialValues={{
        statuses: store.filters.statuses?.map((s) => ({ key: OrderStatusToTextMapping[s], value: String(s) })) ?? [],
      }}>
      {() => (
        <Form>
          <Container fluid>
            <Row className="mb-2 w-auto pb-2 border-bottom align-items-center">
              <Col xs lg={1}>
                Фильтры
              </Col>
              <Col lg="10" className="row align-items-center">
                <div
                  style={
                    // TODO: Убрать хардкод
                    { minWidth: '150px', marginBottom: '-5px' }
                  }>
                  <MultiSuggestField
                    name="statuses"
                    optionLabelKey="key"
                    optionValueKey="value"
                    search={() => Promise.resolve(store.orderStatuses)}
                    placeholder="Статусы"
                  />
                </div>
              </Col>
              <Col xs lg={1}>
                <Button disabled={areOrdersLoading} type="submit" size="sm">
                  Применить
                </Button>
              </Col>
            </Row>
          </Container>

          <WidgetBase
            col={col}
            title={title}
            isLoading={areOrdersLoading}
            isEmpty={total === 0}
            hasError={hasError}
            buttons={[
              // eslint-disable-next-line @typescript-eslint/no-empty-function
              { title: 'Создать', disabled: !canCreateOrders, clickHandler: () => {}, render: renderCreateNewOrderBtn },
            ]}>
            <div className="px-5">
              <WidgetTable>
                <thead>
                  <tr>
                    <th className="border-0 text-tiny pt-0">Корзина</th>
                    <th className="border-0 text-tiny pt-0">Адрес</th>
                    <th className="border-0 text-tiny pt-0">Заказ</th>
                    <th className="border-0 text-tiny pt-0">Исполнитель</th>
                    <th className="border-0 text-tiny pt-0">Стоимость</th>
                  </tr>
                </thead>
                <tbody>
                  <ListContainer ItemComponent={OrderRow} items={getOrdersInformation} />
                </tbody>
              </WidgetTable>
              <PaginationField
                limitList={[5, 10, 50]}
                className={styles.paginationField}
                handlePagination={handlePagination}
                pagination={pagination}
              />
            </div>
          </WidgetBase>
        </Form>
      )}
    </Formik>
  );
});
